import React, { RefObject, useContext, useRef } from 'react';
import { Logger } from 'alfa-web-tools';

import { encryptPhoneNumber } from 'app/utils/StringUtils';
import { appendLogger } from 'app/api/logger';
import { LogoMyFin } from 'app/component/icons/LogoMyFin/LogoMyFin';
import { MYFIN_CHANNEL } from 'app/constants/baseConstants';
import { StepOrder } from 'app/constants/step';
import { DEFAULT_SOURCE, sourceMap } from 'app/constants/utmSource';
import { getChannel } from 'app/utils/LocationUtils';
import { CardProduct } from 'app/model/product/CardProduct';
import { RestoreFormData } from '../../types/store';
import { ProductParametersConfigureFormStore } from '../../logic/pages/productParametersConfigure/ProductParametersConfigureFormStore';
import { EmploymentFormStore } from '../../logic/pages/employment/EmploymentFormStore';

const AppContext = React.createContext<RefObject<ApplicationContext>>(null);

export const useApplicationContext = () => {
    const context = useContext(AppContext);

    return context.current;
};

const DEFAULT_LATEST_STEP = StepOrder.STEP_VIEW_PRODUCT_DATA;

export class ApplicationContext {
    /**
     * Параметры выбранного продукта
     * @private
     */
    private _cardProduct: CardProduct = { code: '', name: '' } as CardProduct;

    /**
     * Идентификатор последней страницы, с которой был сохранен order
     * Используется в случаях перехода по кнопке Назад
     * @private
     */
    private _latestSubmittedStep: number = DEFAULT_LATEST_STEP;

    /**
     * Код ресурса со ссылкой на приложение
     */
    private _utmSource: string = DEFAULT_SOURCE;

    /**
     * Номер телефона пользователя
     */
    private _phoneNumber: string;

    /**
     * Канал продажи (saleChannel)
     */
    channel: string;

    /**
     * Логер ошибок клиента
     */
    logger: Logger;

    /**
     * Пользователь продолжает заполнение заявки, перейдя по ссылке-нотификации
     * @param isDraftContinuationByLink
     */
    isDraftContinuationByLink: boolean;

    /**
     * Внутренний идентификатор заявки
     */
    internalId: string;

    /**
     * Дополнительный логотип для определенных каналов продаж
     */
    additionalLogo: JSX.Element;

    /**
     * Данные, заполненные пользователем на шаге, который еще не был сохранен в printDoc
     * Используется для сохранения состояния формы при возвращении после перехода назад по приложению
     */
    private _latestFilledFormData: RestoreFormData<
        ProductParametersConfigureFormStore | EmploymentFormStore
    > | null;

    constructor(saleChannel: string) {
        this.channel = saleChannel;
        appendLogger();
        this.logger = (window as any).getLogger();

        switch (this.channel) {
            case MYFIN_CHANNEL:
                this.additionalLogo = <LogoMyFin />;
                break;
            default:
                this.additionalLogo = null;
                break;
        }
    }

    get cardProduct(): CardProduct {
        return this._cardProduct;
    }

    get productCode(): string {
        return this._cardProduct.code;
    }

    set cardProduct(value: CardProduct) {
        this._cardProduct = value;
    }

    getCardProductTitle(productName?: string, prefix?: string): string {
        const { name } = this._cardProduct;
        const productTitle = productName || name;
        const productPrefix = prefix || 'Онлайн-заявка';

        return (
            productPrefix +
            (productTitle === '' ? '' : ' «' + productTitle + '»')
        );
    }

    get latestSubmittedStep(): number {
        return this._latestSubmittedStep;
    }

    set latestSubmittedStep(value: number) {
        this._latestSubmittedStep =
            value > this._latestSubmittedStep
                ? value
                : this._latestSubmittedStep;
    }

    /**
     * Определяет, страница открыта впервые, либо на нее попали после навигации назад
     * @param value
     */
    isInitialStepVisit(value: number): boolean {
        return value > this.latestSubmittedStep;
    }

    /**
     * Возвращает порядковый номер шага, на котором остановилось заполнение форм
     */
    get latestFilledFormStep(): number {
        return this._latestSubmittedStep + 1;
    }

    /**
     * Определяет, есть ли несохраненные пользовательские данные на текущей форме
     * @param step Номер шага, с которого вызывается метод
     * @returns
     */
    isFormRestoreAvailable(step: number): boolean {
        return this.latestFilledFormStep === step && this._latestFilledFormData;
    }

    /**
     * Возвращает данные формы с последнего заполненного шага
     * @returns Данные не отправленной в printDoc формы
     */
    get latestFilledFormData(): RestoreFormData<
        ProductParametersConfigureFormStore | EmploymentFormStore
    > {
        const result = { ...this._latestFilledFormData };

        return result;
    }

    /**
     * Устанавливает значение данных формы, если находится на последнем
     * не сохраненном шаге
     */
    setLatestFilledFormData(
        formData: RestoreFormData<
            ProductParametersConfigureFormStore | EmploymentFormStore
        >,
        step: number,
    ) {
        if (this.latestFilledFormStep === step) {
            this._latestFilledFormData = formData;
        }
    }

    /**
     * Устанавливает и дополняет значения полей сохраненных данных формы
     * @param updateParameters параметры, которыми нужно дополнить сохраненные данные формы
     */
    updateLatestFilledFormData(updateParameters: {}) {
        this._latestFilledFormData = {
            ...this._latestFilledFormData,
            ...updateParameters,
        };
    }

    get utmSource() {
        return this._utmSource;
    }

    set utmSource(value: string) {
        this._utmSource = sourceMap[value] || value;
    }

    /**
     * Устанавливает значение номера телефона пользователя.
     * От номера телефона зависит cuid
     * @param value
     */
    set phoneNumber(value: string) {
        this._phoneNumber = value;
    }

    get phoneNumber(): string {
        return this._phoneNumber;
    }

    /**
     * Уникальный идентификатор пользователя, основанный на номере телефона
     */
    get cuid(): string {
        return this._phoneNumber ? encryptPhoneNumber(this._phoneNumber) : '';
    }
}

export const ApplicationContextProvider = ({
    children,
}: {
    children: React.ReactNode;
}) => {
    const saleChannel = getChannel();
    const defaultContext = useRef(new ApplicationContext(saleChannel));

    return (
        <AppContext.Provider value={defaultContext}>
            {children}
        </AppContext.Provider>
    );
};
